import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import { HiFilter, HiSearch } from 'react-icons/hi';
import { parseMetadata } from '../utils/metadataParser';

const ArticleGrid = ({ pins }) => {
  const [articles, setArticles] = useState([]);
  const [displayedArticles, setDisplayedArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);
  const sentinelRef = useRef(null);
  const navigate = useNavigate();
  
  const ITEMS_PER_PAGE = 12;

  useEffect(() => {
    fetchAllArticles();
  }, [pins]);

  useEffect(() => {
    let filtered = articles;
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }
    
    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(article => 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.category.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredArticles(filtered);
    setHasMore(filtered.length > ITEMS_PER_PAGE);
    setDisplayedArticles(filtered.slice(0, ITEMS_PER_PAGE));
  }, [selectedCategory, articles, searchQuery]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || loadingMore || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            console.log('Sentinel in view, loading more articles...');
            loadMoreArticles();
          }
        });
      },
      {
        root: null,
        rootMargin: '200px', // Start loading when sentinel is 200px away from being visible
        threshold: 0,
      }
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [loadingMore, hasMore, displayedArticles.length]);

  const loadMoreArticles = async () => {
    if (loadingMore || !hasMore) {
      console.log('loadMoreArticles blocked:', { loadingMore, hasMore });
      return;
    }
    
    console.log('Starting to load more articles...');
    setLoadingMore(true);
    
    // Add a small delay to show the spinner
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const nextIndex = displayedArticles.length;
    const nextBatch = filteredArticles.slice(nextIndex, nextIndex + ITEMS_PER_PAGE);
    
    console.log(`Loading more: ${nextIndex} to ${nextIndex + ITEMS_PER_PAGE}, batch size: ${nextBatch.length}`);
    
    if (nextBatch.length > 0) {
      setDisplayedArticles(prev => [...prev, ...nextBatch]);
    }
    
    setHasMore(nextIndex + ITEMS_PER_PAGE < filteredArticles.length);
    console.log('Finished loading, setting loadingMore to false');
    setLoadingMore(false);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const getCurrentCategoryDisplay = () => {
    return selectedCategory === 'all' ? 'All Categories' : selectedCategory;
  };

  const toggleSearch = () => {
    setSearchOpen(!searchOpen);
    if (!searchOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const fetchAllArticles = async () => {
    setLoading(true);
    const articlesData = [];

    // Use Promise.all for concurrent loading instead of sequential
    const promises = pins.map(async (pin) => {
      try {
        const response = await fetch(`/content/${pin.markdownFile}`);
        if (!response.ok) return null;
        
        const markdownText = await response.text();
        const { metadata, contentWithoutMetadata } = parseMetadata(markdownText);
        
        // Extract first image from content
        let firstImage = null;
        const imageMatch = contentWithoutMetadata.match(/!\[.*?\]\((.*?)(?:\s+".*?")?\)/);
        if (imageMatch) {
          firstImage = imageMatch[1].trim();
        }

        return {
          id: pin.id,
          title: metadata.title || pin.title,
          description: metadata.description || pin.description,
          category: metadata.category || pin.category,
          date: metadata.date || '',
          image: firstImage,
          markdownFile: pin.markdownFile
        };
      } catch (error) {
        console.error(`Error loading article ${pin.id}:`, error);
        return null;
      }
    });

    const results = await Promise.all(promises);
    const validArticles = results.filter(Boolean);

    setArticles(validArticles);
    
    // Extract unique categories
    const uniqueCategories = [...new Set(validArticles.map(article => article.category).filter(Boolean))];
    setCategories(uniqueCategories);
    
    setFilteredArticles(validArticles);
    setDisplayedArticles(validArticles.slice(0, ITEMS_PER_PAGE));
    setHasMore(validArticles.length > ITEMS_PER_PAGE);
    setLoading(false);
  };

  const handleCardClick = (articleId) => {
    navigate(`/location/${articleId}`);
  };

  // Breakpoint columns for responsive design
  const breakpointColumnsObj = {
    default: 4,
    1400: 3,
    1000: 2,
    600: 1
  };

  if (loading) {
    return (
      <div className="article-grid-loading">
        <div className="loading-spinner"></div>
        <p>Loading articles...</p>
      </div>
    );
  }

  return (
    <>
      <div className="article-grid-container">
        <div className="article-grid-filters">
          <div className="filter-container">
            <div className="custom-dropdown" ref={dropdownRef}>
              <button 
                className="dropdown-toggle"
                onClick={toggleDropdown}
              >
                <HiFilter className="filter-icon" />
                <span className="dropdown-text">{getCurrentCategoryDisplay()}</span>
                <span className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
              {dropdownOpen && (
                <div className="dropdown-menu">
                  <div 
                    className={`dropdown-item ${selectedCategory === 'all' ? 'active' : ''}`}
                    onClick={() => handleCategoryChange('all')}
                  >
                    All Categories
                  </div>
                  {categories.map((category) => (
                    <div 
                      key={category}
                      className={`dropdown-item ${selectedCategory === category ? 'active' : ''}`}
                      onClick={() => handleCategoryChange(category)}
                    >
                      {category}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className={`search-container ${searchOpen ? 'expanded' : ''}`} ref={searchRef}>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search articles..."
                className="search-input"
                style={{ display: searchOpen ? 'block' : 'none' }}
              />
              <button 
                className="search-toggle"
                onClick={toggleSearch}
              >
                <HiSearch className="search-icon" />
              </button>
            </div>
          </div>
        </div>
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className="masonry-grid"
          columnClassName="masonry-grid-column"
        >
          {displayedArticles.map((article) => (
            <div 
              key={article.id}
              className="article-card"
              onClick={() => handleCardClick(article.id)}
            >
              {article.image && (
                <div className="article-card-image">
                  <img 
                    src={article.image} 
                    alt={article.title}
                    onError={(e) => {
                      console.error(`Failed to load image: ${e.target.src}`);
                      e.target.parentElement.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <div className="article-card-content">
                <div className="article-card-category">{article.category}</div>
                <h3 className="article-card-title">{article.title}</h3>
                {article.date && (
                  <div className="article-card-date">{article.date}</div>
                )}
                <p className="article-card-description">
                  {article.description.length > 150 
                    ? article.description.substring(0, 150) + '...'
                    : article.description
                  }
                </p>
              </div>
            </div>
          ))}
        </Masonry>
        
        {/* Invisible sentinel element for intersection observer */}
        {hasMore && (
          <div 
            ref={sentinelRef}
            style={{ height: '1px', width: '100%' }}
            aria-hidden="true"
          />
        )}
      </div>
      
    </>
  );
};

export default ArticleGrid;